import { indoorCategoriesItemOffersAtom, indoorItemNamesAtom } from "@/atoms/indoor";
import { HeaderSticky } from "@/components/header-sticky";
import { CategoryHero } from "@/components/home/CategoryHero";
import { CategoryEmptyState, CategoryItemGrid } from "@/components/home/CategoryItemGrid";
import { ImageModal } from "@/components/modals/ImageModal";
import { BackButton } from "@/components/navigations";
import { filterItemOffersByName } from "@/lib/filter-item-offers-by-name";
import { Category } from "@/types";
import { useAtomValue } from "jotai";
import { useEffect, useMemo, useRef, useState } from "react";
import { SearchEmpty } from "@/components/ui";

/**
 * Category modal body in CATEGORY_LIST mode. Visually identical to delivery
 * CategoryContent: category banner + sticky name header (after scroll > 200) +
 * item search + item grid.
 */
export function MenuCategoryModalContent({
  category,
  handleModalClose,
  handleItemClick,
  modalContentRef,
}: {
  category: Category;
  handleModalClose: () => void;
  handleItemClick: (itemId: string) => void;
  modalContentRef: React.RefObject<HTMLDivElement>;
}) {
  const categoriesItemOffers = useAtomValue(indoorCategoriesItemOffersAtom);
  const itemNames = useAtomValue(indoorItemNamesAtom);
  const offers = useMemo(
    () => categoriesItemOffers[category.id] ?? [],
    [categoriesItemOffers, category.id],
  );

  const imageRef = useRef<HTMLDivElement>(null);
  const [scrollYPosition, setScrollYPosition] = useState(0);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [itemsFilter, setItemsFilter] = useState("");

  const filteredItemOffers = useMemo(() => {
    if (!itemsFilter.trim() || !itemNames) return offers;
    return filterItemOffersByName(offers, itemNames, itemsFilter);
  }, [itemsFilter, itemNames, offers]);

  useEffect(() => {
    const el = modalContentRef.current;
    if (!el) return;
    const handleScroll = () => setScrollYPosition(el.scrollTop);
    el.addEventListener("scroll", handleScroll, { passive: true });
    return () => el.removeEventListener("scroll", handleScroll);
  }, [modalContentRef]);

  const handleImageClick = () => {
    if (category.image?.URL) setIsImageModalOpen(true);
  };

  const isSearching = itemsFilter.trim().length > 0;

  if (offers.length === 0) {
    return <CategoryEmptyState onBack={handleModalClose} />;
  }

  return (
    <>
      <HeaderSticky
        scrollYPosition={scrollYPosition}
        name={category.name}
        setShowSearch={setShowSearch}
        showSearch={showSearch}
        itemsFilter={itemsFilter}
        setItemsFilter={setItemsFilter}
        reactComponent={<BackButton label="Voltar" onClick={handleModalClose} />}
      />

      <div className="mx-auto w-full max-w-shell">
        <CategoryHero category={category} onZoom={handleImageClick} imageRef={imageRef} />
        <CategoryItemGrid
          offers={filteredItemOffers}
          onItemClick={handleItemClick}
          hideQuantityButton
          empty={
            isSearching ? (
              <SearchEmpty
                headingLevel={3}
                className="col-span-full"
                title="Nenhum produto encontrado"
                description={`Não encontramos produtos com o termo "${itemsFilter}".`}
                onClear={() => setItemsFilter("")}
              />
            ) : null
          }
        />
      </div>

      <ImageModal
        isOpen={isImageModalOpen}
        onClose={() => setIsImageModalOpen(false)}
        imageUrl={category.image?.URL}
        crc={category.image?.["CRC-32"]}
        alt={category.name ?? "Imagem da categoria"}
      />
    </>
  );
}
